/**
 * Client-side auth helpers: keys session-scoped client state to the signed-in
 * customer.
 *
 * The Console has NO role-based access control. It is a single-audience customer
 * surface (ADR 0004 / E06) and its only user type is the paying customer; all RBAC
 * lives in Argus, which has its own identity store and its own three roles (E07).
 *
 * This module used to derive a client-side view gate from the token's `role` claim
 * (`getAccess`, `DUAL_ROLES = {operator, admin}`, `canUseView`, `loadView`,
 * `saveView`) which selected between the customer Console and a deprecated
 * operator/research view. ADR 0004 retired that dual view and the removal is done:
 * the shell, its tab registry and its four tabs are gone, and so is the gate.
 *
 * It was never a security boundary and should not be reintroduced as one:
 * `decodeToken` reads the payload with NO signature check and NO `exp` check, so any
 * JWT-shaped string could satisfy a claim test here. Only the backend decides what a
 * request may read. See docs/authentication.md.
 */

/** Decode a JWT payload to a plain object, or null if it can't be parsed. */
export function decodeToken(token) {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** The first value that is neither null/undefined nor the empty string. */
function firstPresent(...values) {
  return values.find((v) => v != null && v !== "") ?? null;
}

/**
 * The key identifying WHOSE session a piece of session-scoped client state
 * belongs to (see `engine/visitedScreens.js`).
 *
 * Read from the credential, never from the login form. The login form only
 * answers "did somebody sign in during THIS page-load"; a reload restores the
 * token, and therefore the session, with no login and no username. A
 * form-derived key is empty exactly when the customer is still signed in, which
 * is the worst possible time for it to be empty.
 *
 * The ladder is most-stable-identifier first:
 *   - `uid`, the account's immutable id. Survives a username change and a token
 *     re-issue for the same account, so a refreshed credential does not read as
 *     a different customer and wipe a live record.
 *   - `sub`, the username. Same account, one indirection less stable.
 *   - the raw token, for a credential carrying neither (a truncated or
 *     hand-edited stored value). It is opaque, and it differs per customer,
 *     which is the whole of what a key has to be. The point of this last rung
 *     is that the function is TOTAL: returning null for a token we merely
 *     failed to parse would reproduce the very bug this exists to prevent, an
 *     authenticated customer with no owner silently dropping everything.
 *
 * Returns null ONLY when there is no token at all, which is offline-auth mode
 * (`VITE_OFFLINE_AUTH`); there the caller falls back to the form username.
 */
export function getSessionKey(token) {
  if (!token || typeof token !== "string") return null;
  const claims = decodeToken(token);
  // Each rung falls through on absent OR empty, which is what makes this the
  // ladder the docblock describes. `??` alone would not: it treats "" as a
  // PRESENT value, so a token carrying `uid: ""` beside a real `sub` skipped
  // straight to the raw token and never consulted the rung below it.
  // A key has to be non-empty to be a key at all (`beginSession` ignores a
  // falsy one), so empty is absent here. `uid: 0` is NOT empty and is a
  // legitimate key, which is why the test is `!== ""` and not falsiness.
  const id = firstPresent(claims?.uid, claims?.sub);
  return id == null ? token : String(id);
}
